import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RiCalendar2Fill } from "react-icons/ri";
import { AiOutlinePlus } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { get } from "../../utility/fetch";
import TagInputs from "../layouts/TagInputs";
import Spinner from "../UI/Spinner";
import { usePatient } from "../../contexts";
import Pagination from "../layouts/Pagination";
import QueuedWalkInTable from "../tables/QueuedWalkInTable";
import QueuedReferralTable from "../tables/QueuedReferralTable";

function ApprovedRequests() {
  const [allPatients, setAllPatients] = useState([]);
  const [allReferral, setAllReferral] = useState([]);
  const [allWalkIn, setAllWalkIn] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [payload, setPayload] = useState('');
  const [filterSelected, setFilterSelected] = useState("firstName");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [queuedloading, setQueuedLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTab, setSelectedTab] = useState('walkin'); // 'walkin' or 'referral'
  const [referralPage, setReferralPage] = useState(1);
  const [walkInPage, setWalkInPage] = useState(1);
  const [referralTotalPages, setReferralTotalPages] = useState(1);
  const [walkInTotalPages, setWalkInTotalPages] = useState(1);

  const itemsPerPage = 10;
  const { setHmoDetails, patientId, nurseRoles } = usePatient();

  const handlePageChange = (newPage) => {
    if (selectedTab === 'walkin') {
      if (newPage > 0 && newPage <= walkInTotalPages) {
        setWalkInPage(newPage);
        getWalkInPatients(newPage);
      }
    } else if (selectedTab === 'referral') {
      if (newPage > 0 && newPage <= referralTotalPages) {
        setReferralPage(newPage);
        getReferralPatients(newPage);
      }
    }
  };

  const generatePageNumbers = (currentPage, totalPages) => {
    let pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages = [1, 2, 3, 4, totalPages];
      } else if (currentPage >= totalPages - 2) {
        pages = [1, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      } else {
        pages = [1, currentPage - 1, currentPage, currentPage + 1, totalPages];
      }
    }
    return pages;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const CustomInput = ({ value, onClick }) => (
    <button
      onClick={onClick}
      onKeyDown={(e) => e.preventDefault()}
      className="custom-datepicker-input flex gap-6 flex-v-center"
    >
      {isToday(selectedDate) ? "Today" : formatDate(selectedDate)}
      <RiCalendar2Fill />
    </button>
  );

  // WALK-IN ENDPOINTS
  const getWalkInPatients = async (page = walkInPage) => {
    setQueuedLoading(true);
    try {
      let res = await get(`/walkinlabrequest/list/true/UnPaid?filterQuery=''&pageNumber=${page}&pageSize=${itemsPerPage}`);
      if (res?.statusCode === 200) {
        setAllWalkIn(res.data?.recordList || []);
        setWalkInTotalPages(res?.data?.metadata?.totalPages || 1);
        setQueuedLoading(false);
      }
     
    } catch (error) {
      console.error('Error fetching walk-in patients:', error);
      setQueuedLoading(false);
    }
  };

  // REFERRAL ENDPOINTS
  const getReferralPatients = async (page = referralPage) => {
    setQueuedLoading(true);
    try {
      // endpoint: /referredpatient/list/{pageNumber}/{pageSize}/un-paid
      let res = await get(`/referredpatient/list/${page}/${itemsPerPage}/un-paid`);
      if (res?.statusCode === 200) {
        setAllReferral(res.data?.recordList || []);
        setReferralTotalPages(res?.data?.metadata?.totalPages || 1);
        setQueuedLoading(false);
      }
      
    } catch (error) {
      console.error('Error fetching referral patients:', error);
      setQueuedLoading(false);
    }
  };

  // useEffect(() => {
  //   getWalkInPatients(walkInPage);
  // }, [walkInPage]);

  // useEffect(() => {
  //   getReferralPatients(referralPage);
  // }, [referralPage]);

  useEffect(() => {
    if (selectedTab === 'walkin') {
      getWalkInPatients(1);
      setWalkInPage(1);
    } else if (selectedTab === 'referral') {
      getReferralPatients(1);
      setReferralPage(1);
    }
  }, [selectedTab]);

  const renderTabContent = (selectedTab) => {
    switch (selectedTab) {
      case "walkin":
        return (
          <QueuedWalkInTable
            isApproval={true}
            loading={queuedloading}
            getProcedures={getWalkInPatients}
            data={allWalkIn}
            readOnly={false}
            requestType="walkin"
          />
        );
      case "referral":
        return (
          <QueuedReferralTable
            isApproval={true}
            loading={queuedloading}
            getProcedures={getReferralPatients}
            data={allReferral}
            readOnly={false}
            requestType="referral"
          />
        );
      default:
        return null;
    }
  };

  const currentPageNum = selectedTab === 'walkin' ? walkInPage : referralPage;
  const currentTotalPages = selectedTab === 'walkin' ? walkInTotalPages : referralTotalPages;

  return (
    <div className="w-100 m-t-40">
      <h1 className="m-t-20">Approve Requests</h1>

      {/* Tab Navigation */}
      <div className="tabs m-t-20 bold-text">
        <div
          className={`tab-item ${selectedTab === 'walkin' ? 'active' : ''}`}
          onClick={() => setSelectedTab('walkin')}
        >
          Walk-In Patients
        </div>
        <div
          className={`tab-item ${selectedTab === 'referral' ? 'active' : ''}`}
          onClick={() => setSelectedTab('referral')}
        >
          Referral Patients
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {renderTabContent(selectedTab)}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPageNum}
        totalPages={currentTotalPages}
        handlePageChange={handlePageChange}
        generatePageNumbers={generatePageNumbers}
      />
    </div>
  );
}

export default ApprovedRequests;
